import { BLEND_MODES, Container, Sprite, Texture } from "pixi.js"
import { BaseRenderable } from "./base_renderable"
import { Coordinate, Entity, WeaponCharacteristics } from "game-common/models"
import { contentSheetResource } from "../content_textures"

// Well short of the bow's 160px reach, with room for a 10Hz server step plus a round trip of a
// 500px/s arrow, so the player's own arrow still counts as just loosed when its first update lands.
const FRESH_LAUNCH_PX = 100

// A projectile's own art (`bulletSpriteId`: an arrow, a web), from the sheet a plain sprite name is
// found in -- the same frame a landed arrow is drawn with, so an arrow is one object from the bow
// to the ground rather than a red bolt that turns into a stick where it lands.
const projectileTexture = (spriteId: string | undefined): Texture | undefined =>
    spriteId ? contentSheetResource("humanoid")?.textures?.[spriteId] : undefined

// An arrow is drawn smaller than its 27px art: at 1:1 it was half as long as the archer is tall. In
// flight and landed alike (EntityRenderingManager's landed arrow), so a shot stays one object.
// Tune live from the console: window.arrowScale = 0.75 (the next arrow fired, and landed, picks it up).
const ARROW_SCALE = 0.75
export const arrowScale = (): number => {
    const live = typeof window !== "undefined" ? (window as any).arrowScale : undefined
    return typeof live === "number" && Number.isFinite(live) && live > 0 ? live : ARROW_SCALE
}

// No art for it: a plain wooden shaft behind the point it flies from.
const SHAFT = 0x6b4f38

// After dark the world is drawn under the night mask, and a wooden arrow sinks into the ground it
// flies over. An additive copy of the arrow's own pixels lifts it -- inside its own silhouette, so it
// reads as an arrow catching what light there is, not a glowing bolt -- by how dark the room is, and
// not at all by day. Tune live from the console: window.arrowNightLift = 1 (the next arrow picks it up).
const NIGHT_LIFT = 0.8
const nightLift = (): number => {
    const live = typeof window !== "undefined" ? (window as any).arrowNightLift : undefined
    return typeof live === "number" && Number.isFinite(live) ? live : NIGHT_LIFT
}

// Speed: fading copies of the whole arrow behind it, so a shot reads as fast. Copies of the arrow
// itself -- head, shaft and fletching in their own colours -- never a streak along the shaft's line or
// a glow, which is what made the old bolt read as a laser. Only long, thin art (an arrow) smears.
// Tune live from the console (the next arrow picks it up):
//   window.arrowSmear = { count: 3, gap: 4, alpha: 0.58, falloff: 0.62 }
// count copies, `gap` px apart; the nearest at `alpha`, each further one `falloff` times the last.
const SMEAR = { count: 3, gap: 4, alpha: 0.58, falloff: 0.62 }
const smear = (): typeof SMEAR => {
    const live = typeof window !== "undefined" ? (window as any).arrowSmear : undefined
    const pick = (key: keyof typeof SMEAR) => {
        const value = live?.[key]
        return typeof value === "number" && Number.isFinite(value) ? value : SMEAR[key]
    }
    return { count: Math.max(0, Math.min(8, Math.round(pick("count")))), gap: pick("gap"), alpha: pick("alpha"), falloff: pick("falloff") }
}

export class Bullet extends BaseRenderable {
    private launchOffset?: Coordinate
    // The additive copies that lift the arrow (and its smear) after dark, each with the strength it
    // is lifted at in full darkness: a smear copy is lifted only as much as it is drawn.
    private lifts: { sprite: Sprite; base: number }[] = []
    // Each drawn piece (the arrow, each smear copy) with how far the tip has to fly before the
    // piece's back end is past the bow. Nothing is drawn behind the bow: the art is a 2D arrow along
    // the screen heading, and the archer is posed in only eight facings, so a shaft still lying
    // behind the bow hand runs through the archer's head or back in every facing but the sides.
    private pieces: { piece: Container; clearPx: number }[] = []

    constructor(color: any = 0xff0000, alpha = 1.0,
        private readonly bowOffset?: (entity: Entity) => Coordinate | undefined, spriteId?: string) {
        super()

        const texture = projectileTexture(spriteId)
        if (texture) {
            // The art points along +x; the heading is this renderable's own rotation. A long, thin
            // projectile is anchored at its tip, so an arrow strikes with its head; anything rounder
            // at its middle.
            const elongated = texture.width >= texture.height * 2
            const scale = elongated ? arrowScale() : 1
            const copy = (alpha: number, back: number) => {
                const piece = new Container()
                piece.x = -back * scale
                const sprite = new Sprite(texture)
                sprite.anchor.set(elongated ? 1 : 0.5, 0.5)
                sprite.scale.set(scale)
                sprite.alpha = alpha
                const lift = new Sprite(texture)
                lift.anchor.copyFrom(sprite.anchor)
                lift.scale.set(scale)
                lift.blendMode = BLEND_MODES.ADD
                lift.visible = false
                piece.addChild(sprite, lift)
                this.addChild(piece)
                this.lifts.push({ sprite: lift, base: alpha })
                // Only a tip-anchored arrow trails back over the archer; rounder art is left alone.
                this.pieces.push({ piece, clearPx: elongated ? (texture.width + back) * scale : 0 })
            }
            const { count, gap, alpha, falloff } = elongated ? smear() : { ...SMEAR, count: 0 }
            // The furthest copy first, so each nearer one draws over the one behind it; the arrow last.
            for (let i = count - 1; i >= 0; i--) copy(alpha * Math.pow(falloff, i), gap * (i + 1))
            copy(1, 0)
            return
        }
        super.lineStyle(1, SHAFT, alpha)
        super.moveTo(-10, 0).lineTo(0, 0)
    }

    /** How dark the room is, as the night mask reads it (1 is broad day): lifts the art to match. */
    setDarkness(darkness: number) {
        const night = 1 - Math.max(0, Math.min(1, Number.isFinite(darkness) ? darkness : 1))
        const strength = Math.max(0, Math.min(1, nightLift() * night))
        for (const { sprite, base } of this.lifts) {
            sprite.alpha = base * strength
            sprite.visible = sprite.alpha > 0.01
        }
    }

    /**
     * Draws only the pieces wholly past the bow, `travelled` px along the flight from where it left
     * it. Until then the release flash at the bow is all there is of the shot (a few frames).
     */
    private revealPast(travelled: number) {
        for (const { piece, clearPx } of this.pieces) piece.visible = travelled >= clearPx
    }

    /**
     * Where this arrow left the bow, if it is still near enough to it to have just been loosed. An
     * arrow first seen further out came into view mid-flight and has no release to show. Call after
     * the first updateRenderedLocationFrom, which is what finds the bow.
     */
    launchPoint(entity: Entity, freshPx = FRESH_LAUNCH_PX): Coordinate | undefined {
        const { origin, location } = entity
        if (entity.spriteId !== WeaponCharacteristics.bow.bulletSpriteId || !origin || !location) return undefined
        // A cross-floor arrow from a shooter on another storey has no bow on this one.
        if (entity.crossFloorProjectile && !this.launchOffset) return undefined
        if (Math.hypot(location.x - origin.x, location.y - origin.y) > freshPx) return undefined
        const offset = this.launchOffset ?? { x: 0, y: 0 }
        return { x: origin.x + offset.x, y: origin.y + offset.y }
    }

    /**
     * Up a hill a shot is drawn from the bow's lift to the lift at the end of its line, `aim` (hill_lift.ts flightAim):
     * `drop`, how much lower that end is drawn than the bow (flightDrop), turns the art to the line it is drawn along.
     * An aimed shot's line runs from the bow through the cursor; one aimed at nothing keeps the bow's offset throughout.
     */
    alongHill(entity: Entity, drop: number, aim: Coordinate) {
        if (!drop || !entity.origin) return
        const offset = entity.projectileTarget ? this.launchOffset ?? { x: 0, y: 0 } : { x: 0, y: 0 }
        this.rotation = Math.atan2(aim.y - entity.origin.y - offset.y + drop, aim.x - entity.origin.x - offset.x)
    }

    updateRenderedLocationFrom(entity: Entity) {
        if (!this.launchOffset && entity.spriteId === WeaponCharacteristics.bow.bulletSpriteId) {
            const grip = this.bowOffset?.(entity)
            if (grip) {
                // Project aimed arrows from the bow through the cursor below.
                const muzzle = entity.crossFloorProjectile ? 0 : (WeaponCharacteristics.bow.muzzleFireOffset ?? 0) + 1
                this.launchOffset = { x: grip.x - Math.cos(entity.movement.angle) * muzzle,
                    y: grip.y - Math.sin(entity.movement.angle) * muzzle }
            }
        }
        if (entity.projectileTarget && entity.origin) {
            const dx = entity.projectileTarget.x - entity.origin.x
            const dy = entity.projectileTarget.y - entity.origin.y
            const lengthSquared = dx * dx + dy * dy
            // Continue the same projection beyond the cursor. Clamping at 1 switches
            // back to the simulation's heading and puts a visible kink in the flight.
            const progress = lengthSquared > 0
                ? Math.max(0, ((entity.location.x - entity.origin.x) * dx +
                    (entity.location.y - entity.origin.y) * dy) / lengthSquared) : 1
            const offset = this.launchOffset ?? { x: 0, y: 0 }
            this.x = entity.location.x + offset.x * (1 - progress)
            this.y = entity.location.y + offset.y * (1 - progress)
            this.rotation = Math.atan2(dy - offset.y, dx - offset.x)
            // Along the drawn flight, which sets out from the bow rather than from the body.
            this.revealPast(Math.hypot(this.x - entity.origin.x - offset.x, this.y - entity.origin.y - offset.y))
            this.addSidecar(entity)
            return
        }
        if (entity.crossFloorProjectile) this.rotation = entity.movement.angle
        const { origin, location } = entity
        this.revealPast(origin ? Math.hypot(location.x - origin.x, location.y - origin.y) : Infinity)
        if (!this.launchOffset) return super.updateRenderedLocationFrom(entity)
        this.x = entity.location.x + this.launchOffset.x
        this.y = entity.location.y + this.launchOffset.y
        this.addSidecar(entity)
    }
}
